import React, { useState } from 'react';
import { X, Key, Check, AlertCircle, Loader2, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import type { UserConnection } from '@/types';

interface ConfirmAcceptInviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invite: UserConnection | null;
  onAccept: (connectionId: string) => Promise<void>;
}

export const ConfirmAcceptInviteDialog: React.FC<ConfirmAcceptInviteDialogProps> = ({
  isOpen,
  onClose,
  invite,
  onAccept,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setCode('');
    setLoading(false);
    setSuccess(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invite) return;

    if (!code.trim()) {
      setError('Inserisci il codice');
      return;
    }

    if (code.trim().length !== 8) {
      setError('Il codice deve essere di 8 caratteri');
      return;
    }

    // Verifica che il codice corrisponda
    if (code.trim().toUpperCase() !== invite.code.toUpperCase()) {
      setError('Codice non corretto');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAccept(invite.id);
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Error accepting invite:', err);
      setError(err instanceof Error ? err.message : 'Errore durante l\'accettazione dell\'invito');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !invite) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-md w-full"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Conferma Accettazione</h2>
              <p className="text-sm text-gray-600 mt-1">
                Inserisci il codice per confermare
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              /* Success State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Connessione Accettata!
                </h3>
                <p className="text-sm text-gray-600">
                  Ora puoi condividere vini con {invite.userAName}
                </p>
              </div>
            ) : (
              <>
                {/* Invite Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-wine-purple/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-wine-purple" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-900 font-medium mb-1">
                        Invito da:
                      </p>
                      <p className="font-semibold text-gray-900">
                        {invite.userAName || 'Utente'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {invite.userAEmail}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Input Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Codice Invito
                    </label>
                    <Input
                      icon={Key}
                      type="text"
                      value={code}
                      onChange={(e) => {
                        setCode(e.target.value.toUpperCase());
                        setError(null);
                      }}
                      placeholder="Es: ABC123XY"
                      maxLength={8}
                      className="text-center text-xl font-mono tracking-wider"
                      disabled={loading}
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Chiedi il codice a chi ti ha inviato l'invito
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <Card padding="md" className="bg-red-50 border border-red-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-red-900">Errore</p>
                          <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Info */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-900">
                      <strong>Importante:</strong> Il codice è necessario per confermare
                      che desideri davvero accettare questa connessione.
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      fullWidth
                      disabled={loading}
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      fullWidth
                      loading={loading}
                      disabled={!code.trim() || code.trim().length !== 8 || loading}
                      icon={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                    >
                      Accetta
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
